import { PublicLayout } from '@/components/PublicLayout'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import {
  Shield,
  Lock,
  Search,
  Eye,
  Server,
  ArrowRight,
  ScanSearch,
  Users,
  FileCheck,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EASE_STANDARD: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94]

// ---------------------------------------------------------------------------
// Bedrock Standard — 3 feature cards, no illustration
// ---------------------------------------------------------------------------

const bedrockFeatures = [
  {
    icon: ScanSearch,
    title: 'Automated Analysis',
    description:
      'Comprehensive scans for known vulnerabilities and static code analysis catch common errors before they ever reach production.',
  },
  {
    icon: Eye,
    title: 'Independent Audit',
    description:
      'A human reviewer inspects your code for logic loopholes, business flaws, and subtle bugs that automated tools miss entirely.',
  },
  {
    icon: FileCheck,
    title: 'Verified Remediation',
    description:
      'Critical issues are fixed and re-checked by a different reviewer. Zero bias, zero shortcuts, zero exceptions.',
  },
]

// ---------------------------------------------------------------------------
// Keystone Protocol — Pre-Build & Post-Build tiers
// ---------------------------------------------------------------------------

const keystonePreBuild = [
  'Three senior developers independently review your code',
  'Roundtable discussion produces a unified resilience report',
  'Conflict-of-interest isolation between reviewers',
  'Architecture-level threat modelling before a line ships',
]

const keystonePostBuild = [
  'Lead Investigator conducts deep-dive forensic audit',
  'Business logic and future risk vector analysis',
  'Recruitment of niche specialists (cryptography, compliance)',
  'Ongoing monitoring recommendations and hardening plan',
]

// ---------------------------------------------------------------------------
// Data Sovereignty — inverted section items
// ---------------------------------------------------------------------------

const sovereigntyPoints = [
  {
    icon: Server,
    label: 'Dummy Data Protocols',
    text: 'Developers work exclusively with synthetic data. They never touch your customer records.',
  },
  {
    icon: Lock,
    label: 'Production Isolation',
    text: 'Once live, the original developer has zero access to your production database.',
  },
  {
    icon: Eye,
    label: 'Log-Only Access',
    text: 'If bugs occur, maintenance partners see scrubbed application logs, never raw data.',
  },
]

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SecurityPage() {
  const prefersReducedMotion = useReducedMotion()

  /* Card stagger variant */
  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: EASE_STANDARD, delay: i * 0.12 },
    }),
  }

  return (
    <PublicLayout>
      <div className="bg-background text-foreground">
        {/* ================================================================= */}
        {/* 1. HERO — Left text + safe.svg illustration on right              */}
        {/* ================================================================= */}
        <section className="relative overflow-hidden px-8 pb-16 pt-[120px]">
          {/* Gradient mesh */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 80% 60% at 20% 40%, rgba(91,146,121,0.07) 0%, transparent 70%), radial-gradient(ellipse 60% 50% at 80% 30%, rgba(232,152,90,0.06) 0%, transparent 70%)',
            }}
          />

          <div className="relative z-10 mx-auto grid max-w-[1120px] grid-cols-1 items-center gap-12 lg:grid-cols-[1fr_1fr]">
            {/* Text column */}
            <div>
              <motion.div
                className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-primary"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, ease: EASE_STANDARD }}
              >
                Safety &amp; Security
              </motion.div>

              <motion.h1
                className="mb-6 text-foreground"
                style={{
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontSize: 'clamp(38px, 5vw, 54px)',
                  fontWeight: 700,
                  lineHeight: 1.08,
                  letterSpacing: '-1.5px',
                }}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
              >
                Multi-Layered
                <br />
                <span className="bg-gradient-to-br from-primary to-[#6BAE8A] bg-clip-text text-transparent">
                  Protection
                </span>
                , Built In.
              </motion.h1>

              <motion.p
                className="mb-9 max-w-[460px] text-[17px] leading-[1.65] text-muted-foreground"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
              >
                Your code, your data, your customers: guarded at every stage by independent audits,
                enforced protocols, and strict data isolation. Not as an add-on. As the foundation.
              </motion.p>

              <motion.div
                initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: EASE_STANDARD, delay: 0.3 }}
              >
                <Link
                  to="#bedrock"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-foreground"
                >
                  See how it works
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </div>

            {/* Illustration column */}
            <motion.div
              className="flex items-center justify-center rounded-2xl border border-border bg-background p-6"
              initial={prefersReducedMotion ? false : { opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.75, ease: EASE_STANDARD, delay: 0.15 }}
            >
              <img
                src="/illustrations/safe.svg"
                alt="Vault and safe illustration representing code security"
                className="h-auto w-full max-w-[440px]"
                loading="eager"
              />
            </motion.div>
          </div>
        </section>

        {/* ================================================================= */}
        {/* 2. BEDROCK STANDARD — No illustration, 3 feature cards            */}
        {/* ================================================================= */}
        <section id="bedrock" className="bg-card px-8 py-[120px]">
          <div className="mx-auto max-w-[1120px]">
            {/* Centered header */}
            <div className="mb-16 text-center">
              <motion.div
                className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-accent"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.65, ease: EASE_STANDARD }}
              >
                Included in Every Project
              </motion.div>

              <motion.h2
                className="mb-4 text-foreground"
                style={{
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontSize: '42px',
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: '-0.5px',
                }}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
              >
                The Bedrock Standard
              </motion.h2>

              <motion.p
                className="mx-auto max-w-[540px] text-[17px] leading-[1.65] text-muted-foreground"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
              >
                Before any project launches, the code undergoes a rigorous three-stage verification.
                No exceptions, no shortcuts.
              </motion.p>
            </div>

            {/* 3 feature cards in a row */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {bedrockFeatures.map((feature, i) => (
                <motion.div
                  key={feature.title}
                  className="group rounded-[20px] border border-border bg-background p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-[0_12px_32px_rgba(45,42,38,0.08)]"
                  variants={cardVariants}
                  custom={i}
                  initial={prefersReducedMotion ? false : 'hidden'}
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.15 }}
                >
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-[14px] bg-primary/[0.1] transition-transform duration-300 group-hover:-rotate-3 group-hover:scale-110">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3
                    className="mb-2.5 text-xl font-bold text-foreground"
                    style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                  >
                    {feature.title}
                  </h3>
                  <p className="text-sm leading-[1.65] text-muted-foreground">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>

            <motion.p
              className="mt-10 text-center text-sm text-muted-foreground"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, ease: EASE_STANDARD }}
            >
              The first two rounds of Bedrock reviews are included at no cost to the client.
            </motion.p>
          </div>
        </section>

        {/* ================================================================= */}
        {/* 3. KEYSTONE PROTOCOL — Two tier cards + centered illustration      */}
        {/* ================================================================= */}
        <section className="bg-background px-8 py-[120px]">
          <div className="mx-auto max-w-[1120px]">
            {/* Section header */}
            <div className="mb-16 text-center">
              <motion.div
                className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-accent"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.65, ease: EASE_STANDARD }}
              >
                High-Assurance Add-On
              </motion.div>

              <motion.h2
                className="mb-4 text-foreground"
                style={{
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontSize: '42px',
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: '-0.5px',
                }}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
              >
                The Keystone Protocol
              </motion.h2>

              <motion.p
                className="mx-auto max-w-[520px] text-[17px] leading-[1.65] text-muted-foreground"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
              >
                For Fintech, Healthcare, or Enterprise clients who need the highest level of
                resilience assurance.
              </motion.p>
            </div>

            {/* Two tier cards side by side */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              {/* Tier 1 — Pre-Build: Keystone Consensus */}
              <motion.div
                className="group rounded-[20px] border border-border bg-card transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-accent hover:shadow-[0_12px_32px_rgba(45,42,38,0.08)]"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, ease: EASE_STANDARD }}
              >
                <div className="border-b border-border px-7 pb-5 pt-7">
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-[12px] bg-accent/[0.1] transition-transform duration-300 group-hover:-rotate-3 group-hover:scale-110">
                    <Users className="h-5 w-5 text-accent" />
                  </div>
                  <div className="mb-1 text-[11px] font-bold uppercase tracking-[2px] text-accent">
                    Pre-Build
                  </div>
                  <h3
                    className="text-[22px] font-bold text-foreground"
                    style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                  >
                    Keystone Consensus
                  </h3>
                </div>
                <ul className="space-y-3.5 p-7">
                  {keystonePreBuild.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Shield className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                      <span className="text-sm leading-relaxed text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Tier 2 — Post-Build: Keystone Forensic */}
              <motion.div
                className="group rounded-[20px] border border-border bg-card transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-[0_12px_32px_rgba(45,42,38,0.08)]"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, ease: EASE_STANDARD, delay: 0.12 }}
              >
                <div className="border-b border-border px-7 pb-5 pt-7">
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-[12px] bg-primary/[0.1] transition-transform duration-300 group-hover:-rotate-3 group-hover:scale-110">
                    <Search className="h-5 w-5 text-primary" />
                  </div>
                  <div className="mb-1 text-[11px] font-bold uppercase tracking-[2px] text-primary">
                    Post-Build
                  </div>
                  <h3
                    className="text-[22px] font-bold text-foreground"
                    style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                  >
                    Keystone Forensic
                  </h3>
                </div>
                <ul className="space-y-3.5 p-7">
                  {keystonePostBuild.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <span className="text-sm leading-relaxed text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Centered visual anchor — secure-server.svg */}
            <motion.div
              className="mx-auto mt-14 flex max-w-[360px] items-center justify-center rounded-2xl border border-border bg-card p-6"
              initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, ease: EASE_STANDARD }}
            >
              <img
                src="/illustrations/secure-server.svg"
                alt="Secure server representing the Keystone Protocol"
                className="h-auto w-full max-w-[300px]"
                loading="lazy"
              />
            </motion.div>
          </div>
        </section>

        {/* ================================================================= */}
        {/* 4. DATA SOVEREIGNTY — Full-width dark inverted section             */}
        {/* ================================================================= */}
        <section className="bg-foreground px-8 py-[120px]">
          <div className="mx-auto max-w-[1120px]">
            <div className="text-center">
              <motion.div
                className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-accent"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.65, ease: EASE_STANDARD }}
              >
                Data Sovereignty
              </motion.div>

              <motion.h2
                className="mb-6 text-background"
                style={{
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontSize: 'clamp(36px, 4.5vw, 48px)',
                  fontWeight: 700,
                  lineHeight: 1.12,
                  letterSpacing: '-1px',
                }}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
              >
                Your Data. Your Rules.
              </motion.h2>

              <motion.p
                className="mx-auto mb-16 max-w-[500px] text-[17px] leading-[1.65]"
                style={{ color: 'rgba(250, 250, 247, 0.6)' }}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
              >
                No developer ever touches your real customer data. Isolation is not a feature we
                sell. It is a rule we enforce.
              </motion.p>
            </div>

            {/* 3 sovereignty points in a row */}
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {sovereigntyPoints.map((point, i) => (
                <motion.div
                  key={point.label}
                  className="text-center"
                  variants={cardVariants}
                  custom={i}
                  initial={prefersReducedMotion ? false : 'hidden'}
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.15 }}
                >
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-[14px] bg-background/[0.1]">
                    <point.icon className="h-6 w-6 text-background" />
                  </div>
                  <h3
                    className="mb-2 text-lg font-bold text-background"
                    style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                  >
                    {point.label}
                  </h3>
                  <p
                    className="mx-auto max-w-[280px] text-sm leading-relaxed"
                    style={{ color: 'rgba(250, 250, 247, 0.55)' }}
                  >
                    {point.text}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ================================================================= */}
        {/* 5. CTA — "Build With Confidence" — clean, no illustration         */}
        {/* ================================================================= */}
        <section className="relative bg-background px-8 py-[140px] text-center">
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(232,152,90,0.05) 0%, transparent 70%)',
            }}
          />

          <div className="relative z-10">
            <motion.h2
              className="mb-4 text-foreground"
              style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 'clamp(36px, 4vw, 48px)',
                fontWeight: 700,
                letterSpacing: '-1px',
              }}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, ease: EASE_STANDARD }}
            >
              Build With Confidence
            </motion.h2>

            <motion.p
              className="mx-auto mb-10 max-w-[440px] text-lg leading-relaxed text-muted-foreground"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
            >
              Post your project for free. Every line of code is verified before you pay a cent.
            </motion.p>

            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
            >
              <motion.div
                className="inline-flex"
                whileHover={
                  prefersReducedMotion
                    ? {}
                    : {
                        y: -2,
                        scale: 1.02,
                        boxShadow: '0 8px 32px rgba(232,152,90,0.35)',
                      }
                }
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Link
                  to="/post"
                  className="inline-flex items-center gap-2.5 rounded-full bg-accent px-12 py-[18px] text-lg font-bold text-accent-foreground shadow-[0_6px_28px_rgba(232,152,90,0.3)] transition-colors"
                >
                  Get Your Project Started
                  <ArrowRight className="h-5 w-5 transition-transform" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </PublicLayout>
  )
}
